import React from 'react';
import HeaderSSR from '../../components/HeaderSSR';
import FooterSSR from '../../components/FooterSSR';

// ============================================================
// COUNTRY GUIDE DATA
// ============================================================

const countryGuides = [
  {
    flag: '\u{1F1FA}\u{1F1F8}',
    country: 'United States',
    diaspora: '~159,000\u2013300,000',
    remittance: '51% of all inflows',
    href: '/guides/how-to-buy-property-kenya-from-usa',
    label: 'USA Guide',
  },
  {
    flag: '\u{1F1EC}\u{1F1E7}',
    country: 'United Kingdom',
    diaspora: '~139,000\u2013200,000',
    remittance: 'Second largest source',
    href: '/guides/buy-property-kenya-from-uk',
    label: 'UK Guide',
  },
  {
    flag: '\u{1F1E8}\u{1F1E6}',
    country: 'Canada',
    diaspora: '~29,000\u2013100,000',
    remittance: 'Growing corridor',
    href: '/guides/how-to-buy-property-kenya-from-canada',
    label: 'Canada Guide',
  },
  {
    flag: '\u{1F1E6}\u{1F1EA}',
    country: 'UAE & Gulf',
    diaspora: 'Significant workforce',
    remittance: 'Combined Gulf corridor',
    href: '/guides/how-to-buy-property-kenya-from-uae',
    label: 'UAE Guide',
  },
];

// ============================================================
// PROCESS STEPS
// ============================================================

const processSteps = [
  { step: 1, what: 'Define your goal', detail: 'Rental income, family home, land appreciation, or development', who: 'You' },
  { step: 2, what: 'Find a verified agent', detail: 'Browse agents, check credentials, start a conversation', who: 'You + Afriqahome agents', whoHref: '/agents' },
  { step: 3, what: 'Appoint a lawyer', detail: 'Independent Kenyan lawyer for due diligence and conveyancing', who: 'You + lawyer' },
  { step: 4, what: 'Grant Power of Attorney', detail: 'Authenticate at a Kenyan embassy/consulate or via apostille', who: 'You + embassy/notary' },
  { step: 5, what: 'Due diligence', detail: 'Land search, Ardhisasa check, encumbrance search, physical site visit', who: 'Lawyer + surveyor' },
  { step: 6, what: 'Sign and pay', detail: "Sale agreement signed by your PoA holder. Funds transferred to lawyer's client account.", who: 'Lawyer + PoA holder' },
  { step: 7, what: 'Register', detail: 'Stamp duty paid, transfer documents submitted, new title deed issued', who: 'Lawyer + Lands Registry' },
];

// ============================================================
// TRUST RESOURCES
// ============================================================

const trustResources = [
  { href: '/guides/ardhisasa-tutorial', text: 'how to verify title deeds using Ardhisasa' },
  { href: '/guides/fake-title-deeds-kenya-warning-signs', text: 'how to spot fake title deeds' },
  { href: '/guides/verify-real-estate-agent-kenya', text: 'how to verify that a real estate agent is licensed' },
  { href: '/guides/property-scams-kenya', text: 'common property scams in Kenya' },
];

// ============================================================
// LISTING LINKS
// ============================================================

const listingLinks = [
  { href: '/for-sale/apartments/nairobi', text: 'Apartments for sale in Nairobi' },
  { href: '/for-sale/houses/nairobi', text: 'Houses for sale in Nairobi' },
  { href: '/for-sale/plots/nairobi', text: 'Plots for sale in Nairobi' },
  { href: '/for-sale', text: 'All properties for sale' },
];

// ============================================================
// MAIN DIASPORA HUB PAGE
// ============================================================

export default function DiasporaPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderSSR />

      <main className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5">
            <li><a href="/" className="hover:text-green-700">Home</a></li>
            <li>/</li>
            <li className="text-gray-900 font-medium">Diaspora</li>
          </ol>
        </nav>

        {/* Hero / Intro */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          Invest in Kenya Property — From Anywhere
        </h1>

        <div className="prose prose-gray max-w-none mb-12">
          <p>
            Over 4 million Kenyans live and work abroad. In 2024, they sent home a record{' '}
            <strong>USD 4.95 billion</strong> in remittances — more than Kenya earns from tea, coffee, and
            tourism combined. In 2025, that figure is projected to surpass USD 7 billion.
          </p>
          <p>
            A growing share of that money is going into property. Land, apartments, family homes — the
            Kenyan diaspora is building wealth back home. But buying property across borders comes with
            real challenges: verifying agents, confirming title deeds, transferring funds safely, and
            navigating tax obligations in two countries.
          </p>
          <p>
            This page is your starting point. We have built country-specific guides covering the legal,
            financial, and practical steps for buying property in Kenya from where you are. Each guide
            covers Power of Attorney requirements, money transfer options, tax considerations, and how to
            protect yourself from fraud.
          </p>
        </div>

        {/* ── Choose Your Country ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Country</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 pr-4 text-sm font-semibold text-gray-700">Country</th>
                  <th className="py-3 pr-4 text-sm font-semibold text-gray-700">Kenyan Diaspora</th>
                  <th className="py-3 pr-4 text-sm font-semibold text-gray-700">Remittance Share</th>
                  <th className="py-3 text-sm font-semibold text-gray-700">Guide</th>
                </tr>
              </thead>
              <tbody>
                {countryGuides.map((g) => (
                  <tr key={g.country} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-semibold text-gray-900">
                      {g.flag} {g.country}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{g.diaspora}</td>
                    <td className="py-3 pr-4 text-gray-600">{g.remittance}</td>
                    <td className="py-3">
                      <a href={g.href} className="font-semibold text-green-700 hover:text-green-800">
                        {g.label} &rarr;
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-gray-600">
            Each guide covers the complete process — from your legal rights as a buyer to the exact
            steps for transferring funds and registering the property in your name.
          </p>
          <p className="mt-3 text-gray-600">
            Looking for the full picture before diving into a country-specific guide? Read our{' '}
            <a
              href="/guides/kenya-diaspora-property-investment"
              className="text-green-700 hover:text-green-800 font-medium"
            >
              comprehensive Kenya Diaspora Property Investment Guide
            </a>{' '}
            — a 9,500-word deep dive covering every aspect of remote property buying.
          </p>
        </section>

        {/* ── Why Diaspora Buyers Use Afriqahome ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Diaspora Buyers Use Afriqahome</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verified Agents</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Every agent on Afriqahome submits documentation for verification. You can review their
                profile, see their active listings, and message them directly — before committing to
                anything. No cold calls, no mystery operators.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Direct Communication</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Contact agents through WhatsApp, phone, or our in-platform messaging system. Ask
                questions, request video tours of properties, and get responses on your schedule —
                whether you are in Dallas, London, Toronto, or Dubai.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparent Listings</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Listings on Afriqahome include property details, photos, location data, and pricing in
                KES. No hidden listings, no &ldquo;call for price&rdquo; games. What you see is what is
                available.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Trust Resources</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We publish free guides on the topics that matter most to remote buyers —{' '}
                {trustResources.map((r, i) => (
                  <React.Fragment key={r.href}>
                    <a href={r.href} className="text-green-700 hover:text-green-800">
                      {r.text}
                    </a>
                    {i < trustResources.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
                . Knowledge is your best protection.
              </p>
            </div>
          </div>
        </section>

        {/* ── The Process at a Glance ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">The Process at a Glance</h2>
          <p className="text-gray-600 mb-6">
            Regardless of which country you are buying from, the core steps are the same:
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 pr-4 text-sm font-semibold text-gray-700">Step</th>
                  <th className="py-3 pr-4 text-sm font-semibold text-gray-700">What Happens</th>
                  <th className="py-3 text-sm font-semibold text-gray-700">Who Is Involved</th>
                </tr>
              </thead>
              <tbody>
                {processSteps.map((s) => (
                  <tr key={s.step} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-semibold text-gray-900">{s.step}. {s.what}</td>
                    <td className="py-3 pr-4 text-gray-600">{s.detail}</td>
                    <td className="py-3 text-gray-600">
                      {s.whoHref ? (
                        <>
                          You +{' '}
                          <a href={s.whoHref} className="text-green-700 hover:text-green-800">
                            Afriqahome agents
                          </a>
                        </>
                      ) : (
                        s.who
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-gray-600">
            Timeline: <strong>2 to 5 months</strong> from first contact to title deed in your name.
          </p>
        </section>

        {/* ── What You Should Know ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What You Should Know Before You Start</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Your citizenship determines your property rights
              </h3>
              <p className="text-gray-600">
                Kenyan citizens (including dual citizens) can buy freehold property without restriction.
                Non-citizens are limited to leasehold (maximum 99 years). This applies regardless of where
                you live. Check our country guides for details specific to your situation.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Never send money directly to a seller
              </h3>
              <p className="text-gray-600">
                Always transfer funds through your lawyer&apos;s client account. This provides a layer of
                protection if the deal falls through. Legitimate sellers and agents understand and expect
                this.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Tax obligations exist in both countries
              </h3>
              <p className="text-gray-600">
                Owning property in Kenya can trigger reporting and tax obligations in your country of
                residence — especially in the US (FBAR, FATCA), UK (HMRC foreign income reporting), and
                Canada (CRA worldwide income). Consult a tax professional in your home country before
                purchasing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Verification is non-negotiable
              </h3>
              <p className="text-gray-600">
                Always conduct a land search, verify the title deed on{' '}
                <a href="/guides/ardhisasa-tutorial" className="text-green-700 hover:text-green-800">
                  Ardhisasa
                </a>
                , and confirm physical boundaries with a licensed surveyor — especially for land purchases.
                A KES 1,000 land search can save you millions.
              </p>
            </div>
          </div>
        </section>

        {/* ── Estimate Your Costs ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Estimate Your Costs</h2>
          <p className="text-gray-600 mb-4">Use our free tools to plan your purchase:</p>
          <ul className="space-y-2">
            <li>
              <a href="/tools/stamp-duty-calculator" className="text-green-700 hover:text-green-800 font-semibold">
                Stamp Duty Calculator
              </a>
              <span className="text-gray-600"> — estimate the government tax on your property purchase</span>
            </li>
            <li>
              <a href="/tools/mortgage-calculator" className="text-green-700 hover:text-green-800 font-semibold">
                Mortgage Calculator
              </a>
              <span className="text-gray-600"> — model monthly payments if you are considering a Kenyan mortgage</span>
            </li>
          </ul>
        </section>

        {/* ── Start Your Search ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Your Search</h2>
          <p className="text-gray-600 mb-4">Browse verified property listings across Kenya:</p>
          <ul className="space-y-2 mb-4">
            {listingLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="text-green-700 hover:text-green-800">
                  {l.text}
                </a>
              </li>
            ))}
          </ul>
          <p className="text-gray-600">
            Or{' '}
            <a href="/agents" className="text-green-700 hover:text-green-800 font-medium">
              find a verified agent
            </a>{' '}
            who can guide you through the process from wherever you are.
          </p>
        </section>

        {/* ── CTA Banner ── */}
        <section className="bg-green-50 rounded-xl p-8 text-center mb-12">
          <p className="text-lg font-bold text-green-800 mb-2">Ready to Invest in Kenya?</p>
          <p className="text-gray-600 mb-4">
            Browse verified listings from EARB-registered agents. Message agents directly. No hidden fees.
          </p>
          <a
            href="/for-sale"
            className="inline-block text-green-700 hover:text-green-800 font-bold text-lg"
          >
            Browse Properties &rarr;
          </a>
        </section>

        {/* Disclaimer */}
        <p className="text-sm text-gray-400 italic">
          This page is for informational purposes only and does not constitute legal, tax, or financial
          advice. Property laws, tax regulations, and exchange rates change. Consult qualified
          professionals before making investment decisions. Last updated: April 2026.
        </p>
      </main>

      <FooterSSR />
    </div>
  );
}
